import { useCallback, useEffect, useState } from 'react';
import { removeProfilePicture, uploadProfilePicture } from '../services/profileService';
import {
    describeAvatarRemoveError,
    describeAvatarUploadError,
    validateAvatarFile,
} from '../utils/avatarValidation';

/**
 * Picking, previewing, saving and removing the profile picture.
 *   selectFile(file) -> validates and shows a preview
 *   save()           -> uploads it (POST /rfp/auth/users/{user_id}/avatar), then calls
 *                       onSaved(updatedProfile) with the profile the server answers with;
 *                       resolves true when it worked
 *   cancel()         -> drops the chosen picture
 *   remove()         -> deletes the saved picture (DELETE /rfp/auth/users/{user_id}/avatar), then
 *                       calls onRemoved(updatedProfile)
 *   busy             -> true while saving or removing
 */
export const useAvatarUpload = (userId, onSaved, onRemoved) => {
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [removing, setRemoving] = useState(false);

    // Preview of the chosen file; released when it changes or the screen closes.
    useEffect(() => {
        if (!file) {
            setPreviewUrl('');
            return undefined;
        }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const selectFile = useCallback((candidate) => {
        setError('');
        if (!candidate) return;
        const problem = validateAvatarFile(candidate);
        if (problem) {
            setError(problem);
            return;
        }
        setFile(candidate);
    }, []);

    const cancel = useCallback(() => {
        setFile(null);
        setError('');
    }, []);

    const save = useCallback(async () => {
        if (!file || saving || removing) return false;
        setSaving(true);
        setError('');
        try {
            const response = await uploadProfilePicture(userId, file);
            setFile(null);
            if (onSaved) onSaved(response?.data);
            return true;
        } catch (uploadError) {
            setError(describeAvatarUploadError(uploadError));
            return false;
        } finally {
            setSaving(false);
        }
    }, [file, saving, removing, userId, onSaved]);

    const remove = useCallback(async () => {
        if (saving || removing) return false;
        setRemoving(true);
        setError('');
        try {
            const response = await removeProfilePicture(userId);
            setFile(null);
            if (onRemoved) onRemoved(response?.data);
            return true;
        } catch (removeError) {
            setError(describeAvatarRemoveError(removeError));
            return false;
        } finally {
            setRemoving(false);
        }
    }, [saving, removing, userId, onRemoved]);

    return {
        previewUrl,
        hasSelection: Boolean(file),
        fileName: file?.name || '',
        fileSize: file?.size || 0,
        error,
        saving,
        removing,
        busy: saving || removing,
        selectFile,
        cancel,
        save,
        remove,
    };
};
